import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  VocabularyTestProps,
  VocabularyTest as VocabularyTestType,
  VocabularyTestQuestion,
} from '@dreamai/types';
import api from '../../../../api';
import './vocabulary-test.scss';

interface VocabularyTestComponentProps extends VocabularyTestProps {
  threadId?: string;
  updateThreadViewVisualDescription?: (
    visualDescription: string,
  ) => Promise<void>;
}

const VocabularyTest: React.FC<VocabularyTestComponentProps> = ({
  testId,
  threadId,
  updateThreadViewVisualDescription,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [test, setTest] = useState<VocabularyTestType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userInput, setUserInput] = useState('');
  const [hintsUsed, setHintsUsed] = useState(0);
  const [showResult, setShowResult] = useState<'correct' | 'incorrect' | null>(
    null,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const fetchTest = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = await getAccessTokenSilently();
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await api.get(`/language-learning/test/${testId}`);

      if (response.data.success) {
        setTest(response.data.test);
      } else {
        setError(response.data.error || 'Failed to load test');
      }
    } catch (err) {
      console.error('Error fetching test:', err);
      setError('Failed to load test');
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, testId]);

  useEffect(() => {
    fetchTest();
  }, [fetchTest]);

  // Update visual description
  useEffect(() => {
    if (updateThreadViewVisualDescription && test) {
      const status =
        test.status === 'completed'
          ? `Completed: ${test.correctCount}/${test.items.length} correct`
          : `Question ${test.currentIndex + 1} of ${test.items.length}`;
      updateThreadViewVisualDescription(
        `${test.language} Vocabulary Test - ${status}`,
      );
    }
  }, [test, updateThreadViewVisualDescription]);

  // Focus input when question changes
  useEffect(() => {
    if (inputRef.current && !showResult) {
      inputRef.current.focus();
    }
  }, [test?.currentIndex, showResult]);

  const currentQuestion: VocabularyTestQuestion | null =
    test && test.currentIndex < test.items.length
      ? test.items[test.currentIndex]
      : null;

  const getHint = useCallback(() => {
    if (!currentQuestion) return '';

    const fullAnswer =
      currentQuestion.direction === 'toTarget'
        ? currentQuestion.word
        : currentQuestion.translation;

    // Use only the first/primary answer for hints (split on / with optional spaces)
    const primaryAnswer = fullAnswer.split(/\s*\/\s*/)[0].trim();

    const hintLevel = hintsUsed + 1;
    const revealCount = Math.ceil((primaryAnswer.length * hintLevel) / 4);

    // Reveal characters progressively
    const hint = primaryAnswer
      .split('')
      .map((char: string, index: number) => {
        if (char === ' ') return ' ';
        if (index < revealCount) return char;
        return '_';
      })
      .join('');

    return hint;
  }, [currentQuestion, hintsUsed]);

  const handleHint = () => {
    if (hintsUsed < 3) {
      setHintsUsed((prev) => prev + 1);
    }
  };

  const normalizeAnswer = (str: string): string => {
    return str
      .toLowerCase()
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove diacritics for comparison
      .replace(/[.,!?;:'"]/g, ''); // Remove punctuation
  };

  // Remove leading articles (the, a, an) from English answers
  const stripArticle = (str: string): string => {
    return str.replace(/^(the|a|an)\s+/i, '').trim();
  };

  // Split translation on " / " or "/" to get all valid alternatives
  const getValidAnswers = (answer: string): string[] => {
    // Split on " / " first (preferred format), then also on "/" (compact format)
    return answer
      .split(/\s*\/\s*/)
      .map((a) => a.trim())
      .filter(Boolean);
  };

  const checkAnswer = async () => {
    if (!test || !currentQuestion || isSubmitting) return;

    const correctAnswer =
      currentQuestion.direction === 'toTarget'
        ? currentQuestion.word
        : currentQuestion.translation;

    // Check if user input matches ANY of the valid alternatives
    const validAnswers = getValidAnswers(correctAnswer);
    const normalizedInput = normalizeAnswer(userInput);

    // For toEnglish direction, also accept answers without articles
    const isToEnglish = currentQuestion.direction === 'toEnglish';

    const isCorrect = validAnswers.some((answer) => {
      const normalizedAnswer = normalizeAnswer(answer);
      if (normalizedAnswer === normalizedInput) return true;

      // For English translations, accept with or without article
      if (isToEnglish) {
        const strippedAnswer = stripArticle(normalizedAnswer);
        const strippedInput = stripArticle(normalizedInput);
        if (strippedAnswer === strippedInput) return true;
        if (strippedAnswer === normalizedInput) return true;
        if (normalizedAnswer === strippedInput) return true;
      }

      return false;
    });

    setShowResult(isCorrect ? 'correct' : 'incorrect');

    if (isCorrect) {
      // Submit the answer to the backend
      setIsSubmitting(true);
      try {
        const token = await getAccessTokenSilently();
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const response = await api.post(
          `/language-learning/test/${testId}/answer`,
          {
            questionIndex: test.currentIndex,
            correct: true,
            hintsUsed,
          },
        );

        if (response.data.success) {
          // Move to next question after a brief delay
          setTimeout(() => {
            setTest(response.data.test);
            setUserInput('');
            setHintsUsed(0);
            setShowResult(null);
          }, 1000);
        }
      } catch (err) {
        console.error('Error recording answer:', err);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleSkip = async () => {
    if (!test || !currentQuestion || isSubmitting) return;

    // Record as incorrect
    setIsSubmitting(true);
    try {
      const token = await getAccessTokenSilently();
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await api.post(
        `/language-learning/test/${testId}/answer`,
        {
          questionIndex: test.currentIndex,
          correct: false,
          hintsUsed: hintsUsed + 1, // Count skip as using all hints
        },
      );

      if (response.data.success) {
        setTest(response.data.test);
        setUserInput('');
        setHintsUsed(0);
        setShowResult(null);
      }
    } catch (err) {
      console.error('Error recording answer:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && userInput.trim()) {
      checkAnswer();
    }
  };

  if (loading) {
    return (
      <div className='vocabulary-test loading'>
        <div className='loading-spinner'>Loading test...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='vocabulary-test error'>
        <div className='error-message'>{error}</div>
        <button onClick={fetchTest} className='retry-button'>
          Retry
        </button>
      </div>
    );
  }

  if (!test) {
    return (
      <div className='vocabulary-test error'>
        <div className='error-message'>Test not found</div>
      </div>
    );
  }

  // Test completed
  if (test.status === 'completed') {
    const percentage = Math.round(
      (test.correctCount / test.items.length) * 100,
    );
    return (
      <div className='vocabulary-test completed'>
        <div className='completion-card'>
          <h2 className='completion-title'>🎉 Test Complete!</h2>
          <div className='completion-stats'>
            <div className='stat-circle'>
              <span className='stat-value'>{percentage}%</span>
              <span className='stat-label'>Score</span>
            </div>
            <div className='stat-details'>
              <div className='stat-item correct'>
                <span className='stat-icon'>✓</span>
                <span>{test.correctCount} correct</span>
              </div>
              <div className='stat-item incorrect'>
                <span className='stat-icon'>✗</span>
                <span>{test.incorrectCount} incorrect</span>
              </div>
            </div>
          </div>
          <p className='completion-message'>
            {percentage >= 90
              ? 'Excellent work! Keep it up!'
              : percentage >= 70
                ? 'Good job! Practice makes perfect.'
                : "Keep practicing, you'll get there!"}
          </p>
        </div>
      </div>
    );
  }

  if (!currentQuestion) {
    return (
      <div className='vocabulary-test error'>
        <div className='error-message'>No more questions</div>
      </div>
    );
  }

  const prompt =
    currentQuestion.direction === 'toTarget'
      ? currentQuestion.translation
      : currentQuestion.word;

  const answer =
    currentQuestion.direction === 'toTarget'
      ? currentQuestion.word
      : currentQuestion.translation;

  const directionLabel =
    currentQuestion.direction === 'toTarget'
      ? `English → ${test.language}`
      : `${test.language} → English`;

  return (
    <div className='vocabulary-test'>
      {/* Progress bar */}
      <div className='test-progress'>
        <div className='progress-bar'>
          <div
            className='progress-fill'
            style={{
              width: `${(test.currentIndex / test.items.length) * 100}%`,
            }}
          />
        </div>
        <span className='progress-text'>
          {test.currentIndex + 1} / {test.items.length}
        </span>
      </div>

      {/* Question card */}
      <div className={`question-card ${showResult || ''}`}>
        <div className='direction-badge'>{directionLabel}</div>

        {/* Image if available */}
        {currentQuestion.imageUrl && (
          <div className='question-image'>
            <img src={currentQuestion.imageUrl} alt={prompt} />
          </div>
        )}

        {/* Prompt */}
        <div className='question-prompt'>
          <span className='prompt-text'>
            {prompt.includes('/') ? (
              <>
                {prompt.split(/\s*\/\s*/).map((p, i, arr) => (
                  <span key={i}>
                    {p.trim()}
                    {i < arr.length - 1 && (
                      <span className='prompt-separator'> or </span>
                    )}
                  </span>
                ))}
              </>
            ) : (
              prompt
            )}
          </span>
        </div>

        {/* Hint display */}
        {hintsUsed > 0 && (
          <div className='hint-display'>
            <span className='hint-label'>Hint:</span>
            <span className='hint-text'>{getHint()}</span>
          </div>
        )}

        {/* Input area */}
        <div className='answer-area'>
          <input
            ref={inputRef}
            type='text'
            className={`answer-input ${showResult || ''}`}
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder='Type your answer...'
            disabled={isSubmitting || showResult === 'correct'}
            autoComplete='off'
            autoCorrect='off'
            autoCapitalize='off'
            spellCheck={false}
          />

          {showResult === 'incorrect' && (
            <div className='incorrect-feedback'>
              <span className='feedback-icon'>✗</span>
              <span>
                Try again! Hint: {getHint()}
                {answer.includes('/') && ' (or another valid translation)'}
              </span>
            </div>
          )}

          {showResult === 'correct' && (
            <div className='correct-feedback'>
              <span className='feedback-icon'>✓</span>
              <span>
                Correct!{' '}
                {answer.includes('/')
                  ? answer.split(/\s*\/\s*/)[0].trim()
                  : answer}
              </span>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className='action-buttons'>
          <button
            className='hint-button'
            onClick={handleHint}
            disabled={hintsUsed >= 3 || isSubmitting}
          >
            💡 Hint ({3 - hintsUsed} left)
          </button>

          <button
            className='check-button'
            onClick={checkAnswer}
            disabled={!userInput.trim() || isSubmitting}
          >
            Check
          </button>

          <button
            className='skip-button'
            onClick={handleSkip}
            disabled={isSubmitting}
          >
            Skip →
          </button>
        </div>
      </div>

      {/* Score summary */}
      <div className='score-summary'>
        <span className='score-correct'>✓ {test.correctCount}</span>
        <span className='score-incorrect'>✗ {test.incorrectCount}</span>
      </div>
    </div>
  );
};

export default VocabularyTest;
